// frontend/src/features/engine/tools/grbas/components/GRBASDistributionGraph.jsx

import { useEffect, useMemo, useRef } from "react";
import * as d3 from "d3";

const SCALE_LABELS = {
  0: "Normal",
  1: "Mild",
  2: "Moderate",
  3: "Severe",
};

export default function GRBASDistributionGraph({
  label,
  userOrdinal,
  markerLabel = "Your rating",
  distribution = {},
}) {
  const ref = useRef(null);

  const data = useMemo(
    () =>
      [0, 1, 2, 3].map((ordinal) => ({
        ordinal,
        label: `${ordinal} · ${SCALE_LABELS[ordinal]}`,
        value: Number(distribution?.[ordinal] ?? 0),
      })),
    [distribution]
  );

  useEffect(() => {
    const container = d3.select(ref.current);

    container.selectAll("*").remove();

    const total = d3.sum(data, (item) => item.value);

    const margin = {
      top: 12,
      right: 115,
      bottom: 18,
      left: 130,
    };

const width = 760;
const rowHeight = 26;

const height =
  data.length * rowHeight +
  margin.top +
  margin.bottom;

const chartWidth =
  width - margin.left - margin.right;

const svg = container
  .append("svg")
  .attr("viewBox", `0 0 ${width} ${height}`)
  .attr("preserveAspectRatio", "xMidYMid meet")
  .style("width", "100%")
  .style("height", "auto");

const x = d3
  .scaleLinear()
  .domain([
    0,
    d3.max(data, (item) => item.value) || 1,
  ])
  .nice()
  .range([0, chartWidth]);

const y = d3
  .scaleBand()
  .domain(data.map((item) => item.ordinal))
  .range([
    margin.top,
    height - margin.bottom,
  ])
  .padding(0.28);

    // =====================================================
    // SELECTED USER ROW
    // =====================================================

    if (
      typeof userOrdinal === "number" &&
      [0, 1, 2, 3].includes(userOrdinal)
    ) {
      svg
        .append("rect")
        .attr("x", 4)
        .attr("y", y(userOrdinal) - 3)
        .attr("width", width - 8)
        .attr("height", y.bandwidth() + 6)
        .attr("rx", 6)
        .attr(
          "fill",
          "rgba(32, 147, 133, 0.08)"
        );

      svg
        .append("circle")
        .attr("cx", 14)
        .attr(
          "cy",
          y(userOrdinal) +
            y.bandwidth() / 2
        )
        .attr("r", 4)
        .attr("fill", "#209385");
    }

    const chart = svg
      .append("g")
      .attr(
        "transform",
        `translate(${margin.left}, 0)`
      );

    // =====================================================
    // BARS
    // =====================================================

    chart
      .selectAll(".grbas-bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "grbas-bar")
      .attr("x", 0)
      .attr(
        "y",
        (item) => y(item.ordinal)
      )
      .attr(
        "width",
        (item) => x(item.value)
      )
      .attr("height", y.bandwidth())
      .attr("rx", 4)
      .attr("fill", "#209385");

    // =====================================================
    // CATEGORY LABELS
    // =====================================================

    svg
      .selectAll(".grbas-category-label")
      .data(data)
      .enter()
      .append("text")
      .attr(
        "class",
        "grbas-category-label"
      )
      .attr(
        "x",
        margin.left - 12
      )
      .attr(
        "y",
        (item) =>
          y(item.ordinal) +
          y.bandwidth() / 2 +
          4
      )
      .attr(
        "text-anchor",
        "end"
      )
      .text((item) => item.label)
      .style(
        "font-size",
        "12px"
      )
      .style(
        "fill",
        "#333"
      );

    // =====================================================
    // VALUE LABELS
    // =====================================================

    chart
      .selectAll(".grbas-value-label")
      .data(data)
      .enter()
      .append("text")
      .attr(
        "class",
        "grbas-value-label"
      )
      .attr(
        "x",
        (item) => x(item.value) + 8
      )
      .attr(
        "y",
        (item) =>
          y(item.ordinal) +
          y.bandwidth() / 2 +
          4
      )
      .text((item) => {
        const percentage =
          total > 0
            ? Math.round(
                (item.value / total) * 100
              )
            : 0;

        return `${item.value} (${percentage}%)`;
      })
      .style(
        "font-size",
        "12px"
      )
      .style(
        "fill",
        "#333"
      );
  }, [data, userOrdinal]);

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          marginBottom: "0.75rem",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          {label}
        </h3>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: "0.8rem",
            color: "#555",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#209385",
            }}
          />

          {markerLabel}
        </div>
      </div>

      <div ref={ref} />
    </div>
  );
}
